// lib/coupons/previewClient.ts
//
// Cliente ÚNICO de `POST /api/online-orders/coupon-preview` — el preview
// PÚBLICO del micrositio. Hermano de `applyClient.ts` (que habla con
// `/api/coupons/apply` mandando `X-POS-Auth`), separado porque el micrositio no
// tiene token que mandar: su cliente es anónimo.
//
// 🔴 Solo UX. El descuento que devuelve se PINTA; el que se COBRA lo decide
// `/api/online-orders/session`, que recomputa desde precios de la BD. Ver la
// cabecera de la ruta.

import { normalizeCouponCode, type CouponRejectReason } from "@/lib/coupons/engine"
import { couponAlcoholListsFromResponse, type CouponApplyTransportReason } from "@/lib/coupons/applyClient"
import type { CouponRequestLines } from "@/lib/coupons/requestLines"

export type CouponPreviewResult =
  | {
      ok: true
      code: string
      discount_type: "percent" | "fixed"
      discount_value: number
      /** Compra mínima del cupón, o null. La pantalla la necesita para decir "te faltan $X". */
      min_subtotal: number | null
      /** Descuento para el subtotal que se mandó. Informativo: la pantalla lo recomputa en vivo. */
      discountAmount: number
      /** A1b (TABC): alcohol del LOCAL. Solo presentes si vinieron NO vacíos. */
      alcoholItemIds?: string[]
      alcoholOptionIds?: string[]
    }
  | {
      ok: false
      reason: CouponRejectReason | CouponApplyTransportReason | string
      minSubtotal?: number
      /** A1b: base SIN alcohol contra la que el servidor midió el mínimo. Solo
       *  presente si excluyó algo (`min_subtotal` con alcohol en el carrito). */
      measuredSubtotal?: number
    }

export interface CouponPreviewInput {
  locationId: string
  /** Código CRUDO tal como lo tecleó el cliente (o como vino en `?coupon=`). */
  code: string
  /** Subtotal de ítems ANTES de descuentos — el mismo contra el que se mide `min_subtotal`. */
  subtotal: number
  /** A1b (TABC): líneas del carrito (micrositeCouponRequestLines). Ausente = body de main. */
  lines?: CouponRequestLines
}

export async function requestCouponPreview(
  input: CouponPreviewInput,
): Promise<CouponPreviewResult> {
  // Se normaliza ACÁ, una sola vez, y no en cada pantalla que llame: "empa20 "
  // y "EMPA20" son el mismo cupón, y el servidor normaliza igual. Un código que
  // no cumple el formato ni siquiera sale a la red — el cliente recibe el
  // mismo `invalid_format` que le daría el servidor, sin gastar una llamada
  // (ni un token del rate limit) en algo que ya sabemos que no puede existir.
  const code = normalizeCouponCode(input.code)
  if (!code) return { ok: false, reason: "invalid_format" }

  try {
    const res = await fetch("/api/online-orders/coupon-preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        locationId: input.locationId,
        code,
        subtotal: input.subtotal,
        ...(input.lines ? { lines: input.lines } : {}),
      }),
    })

    if (res.status === 429) return { ok: false, reason: "rate_limited" }

    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; reason?: unknown; minSubtotal?: unknown; measuredSubtotal?: unknown; discountAmount?: unknown; coupon?: unknown; alcoholItemIds?: unknown; alcoholOptionIds?: unknown }
      | null

    if (!data) return { ok: false, reason: "server_error" }

    if (data.ok !== true) {
      return {
        ok: false,
        reason: typeof data.reason === "string" && data.reason ? data.reason : "server_error",
        minSubtotal: typeof data.minSubtotal === "number" ? data.minSubtotal : undefined,
        ...(typeof data.measuredSubtotal === "number" ? { measuredSubtotal: data.measuredSubtotal } : {}),
      }
    }

    const coupon = data.coupon as
      | { code?: unknown; discount_type?: unknown; discount_value?: unknown; min_subtotal?: unknown }
      | undefined
    if (
      typeof coupon?.code !== "string" ||
      (coupon.discount_type !== "percent" && coupon.discount_type !== "fixed") ||
      typeof coupon.discount_value !== "number" ||
      typeof data.discountAmount !== "number"
    ) {
      // 200 con forma inesperada: tratarlo como éxito dejaría un cupón sin
      // matemática en el carrito y el total mostrado dejaría de ser el cobrado.
      return { ok: false, reason: "server_error" }
    }

    return {
      ok: true,
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      // `min_subtotal` ausente ⇒ null ("no pide mínimo"), nunca 0: un 0 sería
      // indistinguible de "pide mínimo $0" y da lo mismo, pero un `undefined`
      // colado en el estado rompería la comparación de abajo en silencio.
      min_subtotal: typeof coupon.min_subtotal === "number" ? coupon.min_subtotal : null,
      discountAmount: data.discountAmount,
      ...couponAlcoholListsFromResponse(data),
    }
  } catch {
    return { ok: false, reason: "network" }
  }
}
